import React, { useState } from "react";

import { Eye } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Progress } from "~/components/ui/progress";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TablePagination } from "~/components/ui/table-pagination";
import { usePagination } from "~/hooks/usePagination";
import PaymentDetailsDrawer from "~/pages/payments/components/PaymentDetailsDrawer";
import { formatDate } from "~/utils/helpers";

const PAGE_SIZE = 10;

interface LatePaymentsTableProps {
  latePayments: any[];
  totalLatePayments: number;
  isLoading: boolean;
  getLatenessSeverity: (daysLate: number) => "high" | "medium" | "low";
  showFullTable?: boolean;
}

const formatCurrency = (value: number) =>
  typeof value === "number" && !isNaN(value)
    ? `$${Math.round(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : "$0";

const LatePaymentsTable: React.FC<LatePaymentsTableProps> = ({
  latePayments,
  totalLatePayments,
  isLoading,
  getLatenessSeverity,
  showFullTable = false,
}) => {
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(
    null
  );

  const { page, setPage, pageCount, pageItems } = usePagination(
    latePayments || [],
    PAGE_SIZE
  );

  if (isLoading) {
    return (
      <div className="mb-8 w-full rounded-lg border bg-card text-card-foreground shadow-sm">
        <div className="p-6">
          <h3 className="mb-4 text-lg font-semibold">Pagos Atrasados</h3>
          <TableSkeleton rows={5} columns={7} />
        </div>
      </div>
    );
  }

  if (!latePayments || latePayments.length === 0) {
    return (
      <div className="mb-8 w-full rounded-lg border bg-card text-card-foreground shadow-sm">
        <div className="p-6">
          <h3 className="mb-4 text-lg font-semibold">Pagos Atrasados</h3>
          <div className="py-8 text-center">
            <p className="text-muted-foreground">No hay pagos atrasados</p>
          </div>
        </div>
      </div>
    );
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "low":
        return "text-yellow-600 bg-yellow-100";
      case "medium":
        return "text-orange-600 bg-orange-100";
      case "high":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const displayPayments = showFullTable ? pageItems : latePayments.slice(0, 5);

  return (
    <div className="mb-8 w-full rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="p-6">
        <h3 className="mb-4 text-lg font-semibold">Pagos Atrasados</h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>Monto</TableHead>
                <TableHead>Fecha Vencimiento</TableHead>
                <TableHead>Días de Atraso</TableHead>
                <TableHead>Interés Proporcional</TableHead>
                <TableHead>Estado de Pago</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayPayments.map((payment, index) => {
                const severityLabel = getLatenessSeverity(payment.daysLate);
                const severityColor = getSeverityColor(severityLabel);
                const totalAmount = Number(payment.total_amount);
                const paidAmount = Number(payment.paid_amount || 0);
                const proportionalInterest = Number(
                  payment.proportional_interest || 0
                );
                const totalPlusInterest = totalAmount + proportionalInterest;

                let interestPaid = Math.min(paidAmount, proportionalInterest);
                let paidAfterInterest = Math.max(0, paidAmount - interestPaid);
                let principalPaid = Math.min(paidAfterInterest, totalAmount);
                let remainingInterest = Math.max(
                  0,
                  proportionalInterest - interestPaid
                );
                let remainingPrincipal = Math.max(
                  0,
                  totalAmount - principalPaid
                );
                const pendingTotal = Math.max(
                  0,
                  totalPlusInterest - paidAmount
                );

                const progressPercentage =
                  totalPlusInterest > 0
                    ? Math.min(
                        100,
                        Math.max(0, (paidAmount / totalPlusInterest) * 100)
                      )
                    : 0;

                return (
                  <TableRow
                    key={payment.id}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                  >
                    <TableCell>
                      {payment.client?.name || "Cliente desconocido"}
                    </TableCell>
                    <TableCell>
                      ${Math.round(totalAmount).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {formatDate(payment.payment_due_date)}
                    </TableCell>
                    <TableCell>
                      <div
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${severityColor}`}
                      >
                        <span className="font-medium">{payment.daysLate}d</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs font-semibold">
                        {formatCurrency(proportionalInterest)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col space-y-1">
                        <div className="flex justify-between text-xs">
                          <span>
                            {paidAmount > 0
                              ? `$${Math.round(paidAmount).toLocaleString()}`
                              : "Sin pagos"}
                          </span>
                          <span>
                            ${Math.round(totalPlusInterest).toLocaleString()}
                          </span>
                        </div>
                        <Progress value={progressPercentage} className="h-3" />
                        <div className="mt-1 flex justify-between text-xs">
                          <span className="text-muted-foreground">
                            Pendiente:
                          </span>
                          <span className="font-semibold">
                            {formatCurrency(pendingTotal)}
                            {proportionalInterest > 0 && (
                              <span className="ml-2 text-xs text-amber-600">
                                (incl. interés)
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedPaymentId(payment.id)}
                      >
                        <Eye className="mr-1 h-4 w-4" /> Ver detalles
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <div className="mt-4 flex items-center justify-between border-t pt-4">
          <span className="text-sm text-muted-foreground">
            {latePayments.length} pagos atrasados
          </span>
          <span className="font-medium">
            Total: ${Math.round(totalLatePayments).toLocaleString()}
          </span>

          {!showFullTable && latePayments.length > 5 && (
            <Button
              variant="link"
              size="sm"
              onClick={() => (window.location.href = "/late-payments")}
            >
              Ver todos
            </Button>
          )}
        </div>

        {showFullTable && (
          <TablePagination
            page={page}
            pageCount={pageCount}
            totalItems={latePayments.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            itemLabel="pagos atrasados"
            className="mt-4"
          />
        )}
      </div>

      <PaymentDetailsDrawer
        paymentId={selectedPaymentId}
        open={!!selectedPaymentId}
        onClose={() => setSelectedPaymentId(null)}
        payment={latePayments.find((p) => p.id === selectedPaymentId)}
      />
    </div>
  );
};

export default LatePaymentsTable;
